import { AxiosError } from 'axios';
import config from 'config';
import { axiosClient } from 'lib';

import { getToken } from '../helpers';
import { CorporateAudience } from '../types';

type OfferCodePayload = {
  publicCode: string;
  offerCode: string;
};

type OfferCodeResponse = {
  succeeded: boolean;
  message: string;
};

type FailedOfferCodeData = { payload: OfferCodeResponse };

export const validateOfferCode = async ({
  publicCode,
  offerCode,
}: OfferCodePayload): Promise<OfferCodeResponse | null> => {
  try {
    const response = await axiosClient.post(
      `/api/v${config.apiVersion}/CorporateOffers/Validate`,
      {
        publicCode,
        code: offerCode,
      }
    );

    return response.data.payload;
  } catch (error) {
    return (
      (error as AxiosError<FailedOfferCodeData>).response?.data.payload || null
    );
  }
};

type CorpOfferPublicInfo = {
  audienceType: CorporateAudience;
  corporateAccountName: string;
  includeDepartment: boolean;
  backgroundUrl: string | null;
  logoUrl: string | null;
  offerTerm: string;
  publicCode: string;
};

export const getPublicInfo = async (account: string) => {
  const response = await axiosClient.get<{
    payload: { data: CorpOfferPublicInfo };
  }>(`/api/v${config.apiVersion}/CorporateOffers/${account}/PublicInfo`);

  return response.data.payload.data;
};

type GetOfferCodesResponse = {
  accessCode: string | null;
  userCorporateOffer: {
    id: string;
    userId: string;
    corporateAccountId: string;
    code: string;
    publicCode: string;
    backgroundUrl: string | null;
    logoUrl: string | null;
    startDateUTC: string;
    endDateUTC: string;
    createdDate: string;
    createdAt: string;
    updatedAt: string;
  } | null;
};

export const getUserOfferCodes = async (
  userId: string,
  token: string
): Promise<GetOfferCodesResponse> => {
  const offerCodes = await axiosClient.get<{
    payload: { data: GetOfferCodesResponse };
  }>(`/api/v${config.nextApiVersion}/users/${userId}/OfferCodes`, {
    headers: getToken(token),
  });

  return offerCodes.data.payload.data;
};

export const applyOfferCode = async (
  userId: string,
  offerCode: string,
  token: string
): Promise<GetOfferCodesResponse['userCorporateOffer']> => {
  const response = await axiosClient.post(
    `/api/v${config.apiVersion}/users/${userId}/offerCode/${encodeURIComponent(
      offerCode
    )}/apply`,
    undefined,
    {
      headers: getToken(token, true),
    }
  );

  return response.data.payload.data;
};

export const replaceOfferCode = async (
  userId: string,
  offerCode: string,
  token: string
): Promise<GetOfferCodesResponse['userCorporateOffer']> => {
  const response = await axiosClient.put(
    `/api/v${config.apiVersion}/users/${userId}/offerCode/${encodeURIComponent(
      offerCode
    )}/replace`,
    undefined,
    {
      headers: getToken(token, true),
    }
  );

  return response.data.payload.data;
};
